import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { DeleteStudyDialogProps } from '@/types/components/delete-study-dialog'

export function DeleteStudyDialog({
  open,
  onOpenChange,
  studyName,
  deleting,
  onCancel,
  onConfirm,
  mode = 'delete',
}: DeleteStudyDialogProps) {
  const { t } = useTranslation(['app', 'common'])
  const titleByMode = {
    delete: t('studyLobby.deleteStudyQ'),
    archive: t('studyLobby.deleteDraftQ'),
    conversation: t('studyLobby.deleteConversationQ'),
    'quick-study': t('studyLobby.deleteQuickStudyQ'),
  } as const
  const title = titleByMode[mode]
  const description = mode === 'delete'
    ? t('studyLobby.deletePermanentWithData', { name: studyName })
    : t('studyLobby.deletePermanent', { name: studyName })

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="w-[540px] gap-0 rounded-[16px] border-border bg-background p-0 shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end p-3">
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('close', { ns: 'common' })}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-7 px-[60px] pb-10">
          <AlertDialogHeader className="items-center gap-3 space-y-0">
            <AlertDialogTitle className="font-display text-2xl font-normal leading-[1.2] tracking-[-0.48px] text-foreground text-center">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base leading-[1.6] text-muted-foreground text-center">
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex items-center justify-center gap-4">
            <AlertDialogAction
              variant="destructive"
              onClick={onConfirm}
              disabled={deleting}
              className="h-10 rounded-[6px] px-4 text-base font-medium"
            >
              {deleting ? t('deleting', { ns: 'common' }) : t('yesDelete', { ns: 'common' })}
            </AlertDialogAction>
            <AlertDialogCancel
              onClick={onCancel}
              className="h-10 rounded-[6px] border-border bg-transparent px-4 text-base font-medium text-foreground hover:bg-muted hover:text-foreground"
            >
              {t('neverMind', { ns: 'common' })}
            </AlertDialogCancel>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
